import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { SECURITY_FALLBACKS } from '../../../../../../corelogic/domain/constants/displayFallbacks.constants';
import { FrontendTournament } from '../../../../../../corelogic/domain/models/tournament.model';
import { formatDate } from '../../../../../../corelogic/domain/utils/date.utils';
import { SafeText } from '../../../../../../corelogic/domain/utils/xssProtection.utils';
import LoadingBar from '../../Shared/LoadingBar';
import PulsingActiveIcon from '../../Shared/PulsingActiveIcon/PulsingActiveIcon';
import { SecureTextField } from '../../Shared/SecureTextField/SecureTextField';

const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      bgcolor: 'rgba(20, 20, 20, 0.95)',
      color: '#ffffff',
      borderRadius: 2,
      maxWidth: '700px',
      minWidth: {
        xs: '100%',
        sm: '500px',
      },
      width: {
        xs: '100%',
        sm: '90%',
      },
      minHeight: '550px',
      maxHeight: '90vh',
      border: '1px solid rgba(120, 120, 120, 0.4)',
      backgroundImage:
        'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.8)',
      overflowY: 'hidden',
      '&::-webkit-scrollbar': {
        width: '0',
      },
      '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.3)',
        borderRadius: '4px',
        '&:hover': {
          background: 'rgba(255, 255, 255, 0.5)',
        },
      },
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    bgcolor: 'rgba(0, 0, 0, 0.7)',
    borderBottom: '1px solid rgba(204, 0, 0, 0.3)',
    pb: 1,
    p: 2,
  },
  dialogContent: {
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    maxHeight: '80vh',
    bgcolor: 'rgba(0, 0, 0, 0.9)',
  },
  title: {
    fontFamily: 'Rubik Mono One',
    fontSize: 'clamp(1rem, 1.5vw, 1.3rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    '& .MuiSvgIcon-root': {
      fontSize: 'clamp(1.8rem, 2.2vw, 2rem)',
      color: '#cc0000',
    },
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  refreshButton: {
    color: '#cccccc',
    borderColor: 'rgba(204, 0, 0, 0.4)',
    fontSize: 'clamp(0.9rem, 0.9vw, 1rem)',
    '&:hover': {
      color: '#ffffff',
      borderColor: '#cc0000',
      bgcolor: 'rgba(204, 0, 0, 0.1)',
    },
  },
  closeButton: {
    color: '#cccccc',
    '&:hover': {
      color: '#ffffff',
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  searchContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 1,
    width: '100%',
    mb: 1,
    mt: 2,
  },
  searchField: {
    mb: 2,
    flex: 1,
    width: '100%',
    '& .MuiInputLabel-root': {
      color: '#cccccc',
    },
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      height: {
        xs: '36px',
        sm: '38px',
        md: '40px',
        lg: '42px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(204, 0, 0, 0.5)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    '& .MuiInputBase-input': {
      bgcolor: 'rgba(0, 0, 0, 0.3)',
      padding: '8px 12px',
      height: '100%',
      fontSize: '0.9rem',
    },
  },
  filtersContainer: {
    display: 'flex',
    justifyContent: 'left',
    width: '100%',
    mt: 0.5,
    mb: 1,
  },
  toggleButtonGroup: {
    '& .MuiToggleButtonGroup-grouped': {
      border: '1px solid rgba(204, 0, 0, 0.4)',
      color: '#cccccc',
      backgroundColor: 'rgba(30, 30, 30, 0.7)',
      height: {
        xs: '28px',
        sm: '30px',
        md: '32px',
        lg: '34px',
      },
      fontSize: 'clamp(0.65rem, 0.75vw, 0.8rem)',
      fontWeight: 'medium',
      '& .MuiSvgIcon-root': {
        fontSize: '1rem',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(204, 0, 0, 0.2)',
        color: '#ffffff',
        fontWeight: 'bold',
        '&:hover': {
          backgroundColor: 'rgba(204, 0, 0, 0.25)',
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(50, 50, 50, 0.7)',
      },
      '&:not(:first-of-type)': {
        borderLeft: '1px solid rgba(204, 0, 0, 0.4)',
      },
    },
  },
  resultsContainer: {
    mt: 2,
    height: {
      xs: '300px',
      sm: '350px',
      md: '400px',
      lg: '450px',
      xl: '500px',
    },
    overflowY: 'auto',
    pr: 1,
    position: 'relative',
  },
  tournamentItem: {
    p: 1.5,
    px: 2,
    mb: 1.5,
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: {
      xs: '64px',
      sm: '68px',
      md: '72px',
      lg: '76px',
    },
    width: '100%',
    overflow: 'hidden',
    transition: 'all 0.2s',
    bgcolor: 'rgba(40, 40, 40, 0.7)',
    border: '1px solid rgba(70, 70, 70, 0.8)',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
    cursor: 'pointer',
    '&:hover': {
      bgcolor: 'rgba(50, 50, 50, 0.7)',
      transform: 'translateX(3px)',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
    },
  },
  tournamentItemSelected: {
    p: 1.5,
    px: 2,
    mb: 1.5,
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: {
      xs: '64px',
      sm: '68px',
      md: '72px',
      lg: '76px',
    },
    width: '100%',
    overflow: 'hidden',
    transition: 'all 0.2s',
    border: '1px solid rgba(204, 0, 0, 0.6)',
    bgcolor: 'rgba(204, 0, 0, 0.08)',
    position: 'relative',
    cursor: 'pointer',
    '&:hover': {
      bgcolor: 'rgba(204, 0, 0, 0.12)',
      transform: 'translateX(3px)',
      boxShadow: '0 2px 6px rgba(204, 0, 0, 0.25)',
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '3px',
      backgroundColor: '#cc0000',
    },
  },
  tournamentName: {
    fontFamily: 'Bebas Neue',
    fontSize: 'clamp(1.2rem, 1.4vw, 1.5rem)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    letterSpacing: '0.05em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '& .MuiSvgIcon-root': {
      color: '#cccccc',
      flexShrink: 0,
    },
  },
  tournamentInfo: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1,
    overflow: 'hidden',
    minWidth: 0,
  },
  infoGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  infoText: {
    fontSize: 'clamp(0.7rem, 0.9vw, 0.8rem)',
    color: '#cccccc',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    '& .MuiSvgIcon-root': {
      fontSize: 'clamp(0.7rem, 0.8vw, 0.75rem)',
      color: '#aaaaaa',
      marginRight: '2px',
    },
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mt: 0.5,
    flexWrap: 'wrap',
  },
  passwordSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    padding: 1,
    borderRadius: 1,
    bgcolor: 'rgba(25, 25, 25, 0.95)',
    border: '1px solid rgba(204, 0, 0, 0.3)',
    position: 'absolute',
    top: '100%',
    right: 0,
    zIndex: 100,
    mt: 1,
    minWidth: 200,
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
  },
  passwordField: {
    '& .MuiInputBase-root': {
      height: {
        xs: '32px',
        sm: '34px',
        md: '36px',
      },
      fontSize: '0.8rem',
      backgroundColor: 'rgba(20, 20, 20, 0.8)',
    },
    '& .MuiOutlinedInput-root': {
      color: '#ffffff',
      '& fieldset': {
        borderColor: 'rgba(204, 0, 0, 0.5)',
      },
      '&:hover fieldset': {
        borderColor: '#cc0000',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#cc0000',
      },
    },
    flexGrow: 1,
  },
  joinButton: {
    background: 'linear-gradient(45deg, #990000 30%, #cc0000 90%)',
    color: '#ffffff',
    borderRadius: '4px',
    padding: '6px 12px',
    fontFamily: 'Bebas Neue',
    letterSpacing: '0.05em',
    fontSize: 'clamp(0.75rem, 0.9vw, 0.9rem)',
    transition: 'all 0.2s',
    minWidth: '120px',
    '&:hover': {
      background: 'linear-gradient(45deg, #770000 30%, #aa0000 90%)',
      boxShadow: '0 2px 8px rgba(204, 0, 0, 0.4)',
    },
  },
  passwordInputContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: 0.5,
  },
  confirmIconButton: {
    color: '#cc0000',
    padding: '4px',
    bgcolor: 'rgba(204, 0, 0, 0.1)',
    border: '1px solid rgba(204, 0, 0, 0.3)',
    '&:hover': {
      bgcolor: 'rgba(204, 0, 0, 0.2)',
    },
  },
  actionWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 1,
    minWidth: '180px',
    position: 'relative',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 4,
    minHeight: '300px',
    gap: 2,
  },
};

interface SearchTournamentModalProps {
  open: boolean;
  onClose: () => void;
  tournaments: FrontendTournament[];
  isLoading: boolean;
  onJoinTournament: (tournamentId: string, password?: string) => void;
  onRefresh: () => void;
}

const SearchTournamentModal = ({
  open,
  onClose,
  tournaments,
  isLoading,
  onJoinTournament,
  onRefresh,
}: SearchTournamentModalProps) => {
  // States
  const [searchTerm, setSearchTerm] = useState('');
  const [modalFilteredTournaments, setModalFilteredTournaments] = useState<
    FrontendTournament[]
  >([]);
  const [modalFilterType, setModalFilterType] = useState<
    'all' | 'public' | 'private'
  >('all');
  const [selectedTournament, setSelectedTournament] = useState<string | null>(
    null,
  );
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Filter tournaments based on sarch term and filters
  const filterTournaments = (term: string) => {
    let filtered = [...tournaments];

    filtered = filtered.filter((tournament) => {
      // If tournament has ended (not active and has an end date in the past)
      if (!tournament.isActive && tournament.endDate) {
        const endDate = new Date(tournament.endDate);
        const today = new Date();
        if (endDate < today) {
          return false;
        }
      }
      return true;
    });

    // Filter by search term
    if (term) {
      filtered = filtered.filter((t) =>
        t.name.toLowerCase().includes(term.toLowerCase()),
      );
    }

    // Filter by privacy type
    if (modalFilterType !== 'all') {
      filtered = filtered.filter((t) =>
        modalFilterType === 'private' ? t.private : !t.private,
      );
    }

    setModalFilteredTournaments(filtered);
  };

  // Update filters when search term changes
  useEffect(() => {
    filterTournaments(searchTerm);
  }, [searchTerm, tournaments, modalFilterType]);

  // Handle tournament selection
  const handleSelect = (tournamentId: string) => {
    setSelectedTournament(tournamentId);
    setPassword('');
    setShowPassword(false);
  };

  // Handle join button click -> passes info to join with password or join tournament handler directly
  const handleJoin = (tournamentId: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }

    const tournament = tournaments.find((t) => t.id === tournamentId);
    if (!tournament) return;

    // Set selected tournament
    setSelectedTournament(tournamentId);

    // For private tournaments, show password field
    if (tournament.private) {
      setShowPassword(true);
    } else {
      // If public, join directly
      handleJoinTournament(tournamentId);
    }
  };

  // Handle join with password
  const handleJoinWithPassword = () => {
    if (selectedTournament && password) {
      handleJoinTournament(selectedTournament, password);
    }
  };

  // Common function to handle joining tournaments
  const handleJoinTournament = (tournamentId: string, password?: string) => {
    onJoinTournament(tournamentId, password);

    // Clean up
    setShowPassword(false);
    setPassword('');
    handleClose();
  };

  // Handle modal close
  const handleClose = () => {
    setSearchTerm('');
    setSelectedTournament(null);
    setPassword('');
    setShowPassword(false);
    onClose();
  };

  // Handle filter changes
  const handleFilterTypeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilterType: 'all' | 'public' | 'private' | null,
  ) => {
    if (newFilterType !== null) {
      setModalFilterType(newFilterType);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="search-tournament-dialog-title"
      sx={styles.dialog}
      maxWidth="md"
      fullWidth
    >
      {/* Loading Bar at the top of the dialog */}
      <LoadingBar
        isLoading={isLoading}
        position="absolute"
        attachToHeader={false}
      />

      <DialogTitle sx={styles.dialogTitle}>
        <Typography sx={styles.title}>
          <SearchIcon sx={{ color: '#cc0000' }} />
          Find Tournaments
        </Typography>
        <Box sx={styles.headerActions}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={styles.refreshButton}
          >
            Refresh
          </Button>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={styles.dialogContent}>
        <Box sx={styles.searchContainer}>
          {/* Search Field */}
          <SecureTextField
            fullWidth
            placeholder="Search tournaments by name..."
            value={searchTerm}
            onChange={(value) => setSearchTerm(value)}
            variant="outlined"
            fieldType="text"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#cc0000' }} />
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />

          {/* Filters */}
          <Box sx={styles.filtersContainer}>
            <ToggleButtonGroup
              value={modalFilterType}
              exclusive
              onChange={handleFilterTypeChange}
              aria-label="tournament type"
              size="small"
              sx={styles.toggleButtonGroup}
            >
              <ToggleButton value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ViewListIcon fontSize="small" />
                  <Typography variant="body2">ALL</Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="public">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PublicIcon fontSize="small" />
                  <Typography variant="body2">PUBLIC</Typography>
                </Box>
              </ToggleButton>
              <ToggleButton value="private">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon fontSize="small" />
                  <Typography variant="body2">PRIVATE</Typography>
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Results */}
        <Box sx={styles.resultsContainer}>
          {modalFilteredTournaments.length > 0 ? (
            <List>
              {modalFilteredTournaments.map((tournament) => {
                const isSelected = selectedTournament === tournament.id;
                const isPrivateTournament = tournament.private;

                return (
                  <Paper
                    key={tournament.id}
                    elevation={0}
                    sx={
                      isSelected
                        ? styles.tournamentItemSelected
                        : styles.tournamentItem
                    }
                    onClick={() => handleSelect(tournament.id)}
                  >
                    <Box sx={styles.tournamentInfo}>
                      <Typography sx={styles.tournamentName}>
                        {isPrivateTournament ? (
                          <LockIcon
                            sx={{ fontSize: '1.1rem', color: '#cccccc' }}
                          />
                        ) : (
                          <PublicIcon
                            sx={{ fontSize: '1.1rem', color: '#cccccc' }}
                          />
                        )}
                        <SafeText
                          text={tournament.name}
                          fallback={SECURITY_FALLBACKS.TOURNAMENT_NAME}
                        />
                      </Typography>

                      <Box sx={styles.status}>
                        <Box sx={styles.infoGroup}>
                          {tournament.isActive ? (
                            <PulsingActiveIcon />
                          ) : (
                            <CalendarTodayIcon
                              sx={{ fontSize: '0.9rem', color: '#aaaaaa' }}
                            />
                          )}
                          <Typography sx={styles.infoText}>
                            {tournament.isActive
                              ? `${formatDate(tournament.startDate)} - ${tournament.endDate ? formatDate(tournament.endDate) : 'TBC'}`
                              : `Ended on ${formatDate(tournament.endDate)}`}
                          </Typography>
                        </Box>
                        <Box sx={styles.infoGroup}>
                          <PeopleIcon
                            sx={{ fontSize: '0.9rem', color: '#aaaaaa' }}
                          />
                          <Typography sx={styles.infoText}>
                            {tournament.participants || 0}
                          </Typography>
                        </Box>

                        <Box sx={styles.infoGroup}>
                          <EmojiEventsIcon
                            sx={{ fontSize: '0.9rem', color: '#aaaaaa' }}
                          />
                          <Typography sx={styles.infoText}>
                            {tournament.seasonIds?.length || 1}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Password section */}
                    <Box sx={styles.actionWrapper}>
                      {isPrivateTournament && isSelected && showPassword ? (
                        // Show password field when JOIN was clicked
                        <Box
                          sx={styles.passwordInputContainer}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <SecureTextField
                            placeholder="Enter password"
                            type="password"
                            value={password}
                            onChange={(value) => setPassword(value)}
                            size="small"
                            autoFocus
                            sx={styles.passwordField}
                            fieldType="text"
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleJoinWithPassword();
                              }
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon
                                    sx={{
                                      fontSize: '0.9rem',
                                      color: '#cc0000',
                                    }}
                                  />
                                </InputAdornment>
                              ),
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleJoinWithPassword();
                            }}
                            sx={styles.confirmIconButton}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleJoin(tournament.id, e);
                          }}
                          sx={styles.joinButton}
                        >
                          JOIN
                        </Button>
                      )}
                    </Box>
                  </Paper>
                );
              })}
            </List>
          ) : (
            <Box sx={styles.emptyState}>
              <SearchIcon
                sx={{ fontSize: 40, color: '#cc0000', opacity: 0.7, mb: 2 }}
              />
              <Typography
                variant="body1"
                sx={{ color: '#cccccc', fontFamily: 'Arial' }}
              >
                No tournaments found matching your search.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SearchTournamentModal;
